// app/frontend/components/ui/Inputs/ThemedTextInput.tsx
import React from 'react';
import { TextInput, TextInputProps } from '@mantine/core';
import '../../../styles/ThemedInputs.css';
import { ThemedText } from '../../Typography';
import { COLORS, INPUT_LABEL_FONT_SIZE } from '../../../src/constants/theme';

interface ThemedTextInputProps extends Omit<TextInputProps, 'styles'> {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  type?: string;
  description?: string;
  error?: React.ReactNode;
  successMessage?: string;
  /** Per-part overrides merged over the themed defaults (e.g. a big, centered code input). */
  styles?: Partial<Record<InputPart, React.CSSProperties>>;
}

type InputPart = 'label' | 'description' | 'error' | 'input';

const ThemedTextInput: React.FC<ThemedTextInputProps> = ({ successMessage, style, styles, ...props }) => {
  // Determine if field is valid (has value, no error, and has success message)
  const hasValue = props.value && props.value.toString().trim().length > 0;
  const isValid = successMessage && !props.error && hasValue;

  // Extract flex-related styles for the wrapper div so it participates in flex layouts
  const css = style && typeof style === 'object' && !Array.isArray(style) ? (style as React.CSSProperties) : undefined;
  const wrapperStyle: React.CSSProperties = {};
  if (css?.flex) wrapperStyle.flex = css.flex;
  if (css?.minWidth !== undefined) wrapperStyle.minWidth = css.minWidth;
  if (css?.width) wrapperStyle.width = css.width;
  if (css?.maxWidth) wrapperStyle.maxWidth = css.maxWidth;

  return (
    <div style={Object.keys(wrapperStyle).length > 0 ? wrapperStyle : undefined}>
      <TextInput
        {...props}
        style={style}
        // Keep a caller's class alongside ours; a fixed string dropped it.
        className={['themed-input', props.className].filter(Boolean).join(' ')}
        styles={{
          label: {
            color: '#f2f2f2',
            marginBottom: '4px',
            fontWeight: 500,
            fontSize: INPUT_LABEL_FONT_SIZE,
            ...styles?.label,
          },
          description: {
            color: COLORS.textDimmed,
            fontSize: '0.75rem',
            marginTop: '3px',
            lineHeight: 1.4,
            ...styles?.description,
          },
          error: {
            color: '#ff6b6b',
            fontSize: '10px', // 14px
            marginTop: '4px',
            ...styles?.error,
          },
          input: {
            backgroundColor: 'rgba(30, 30, 30, 0.6)',
            borderColor: isValid ? '#22c55e' : props.error ? '#ff6b6b' : 'rgba(255, 255, 255, 0.22)',
            fontSize: '1rem', // 16px
            color: '#f2f2f2',
            transition: 'border-color 0.2s ease',
            '&:focus': {
              borderColor: isValid ? '#22c55e' : props.error ? '#ff6b6b' : '#EA580C',
            },
            '&::placeholder': {
              fontSize: '14px',
              color: COLORS.textDimmed,
            },
            ...styles?.input,
          },
        }}
      />
      {isValid && successMessage && (
        <ThemedText
          size="sm"
          color="#22c55e"
          style={{ fontSize: '10px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <span style={{ fontSize: '0.875rem' }}>✓</span>
          {successMessage}
        </ThemedText>
      )}
    </div>
  );
};

export default ThemedTextInput;
